import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { IoArrowForwardCircle, IoHeart, IoPower } from "react-icons/io5";
import Main from "../../components/layout/Main";
import { useUser } from "../../providers/UserProvider";
import { User } from "../../storage/store";
import DeviceCard, { CardSkeletons } from "../../components/device/DeviceCard";
import PropertyPicker from "../../components/layout/app/PropertyPicker";
import usePropertyDevices from "../../hooks/usePropertyDevices";
import usePushNotification from "../../hooks/usePushNotification";
import { RecentAutomationsSection } from "../Automation/RecentAutomations";
import HomeAddressCard from "../Settings/HomeAddressCard";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import NetworkAlert from "../../components/common/NetworkAlert";

// The app's greeting (app/screens/FirstScreen.js).
const greeting = (hour = new Date().getHours()) => {
  if (hour >= 22) return "Working late";
  if (hour >= 18) return "Good evening";
  if (hour >= 12) return "Good afternoon";
  if (hour >= 5) return "Good morning";
  return "Whoa, early bird";
};

const isFavourite = (device, username) => Boolean(device.usersFavourite?.includes(username));

export const SectionHeading = ({ title, icon: Icon, onClick }) => (
  <h2 style={{ margin: 0 }}>
    <button type="button" className="hx-section-head" onClick={onClick}>
      {title}
      <Icon aria-hidden="true" />
    </button>
  </h2>
);

const DeviceRow = ({ devices, emptyIcon: EmptyIcon, emptyText }) => (
  <div className="hx-row">
    {devices === null ? (
      <CardSkeletons count={4} />
    ) : devices.length ? (
      devices.map((device) => <DeviceCard key={device.applianceId ?? device.deviceId} device={device} />)
    ) : (
      <div className="hx-empty">
        <EmptyIcon aria-hidden="true" />
        {emptyText}
      </div>
    )}
  </div>
);

// Home as in the app: greeting and house picker, the add-address card for
// owners, Recent Automations (where Quick Actions were), Favourites and
// Running Devices.
function Home() {
  const navigate = useHistory();
  const { profileName } = useUser();
  const { devices, error, propertyId } = usePropertyDevices();
  const house = profileName.find((item) => item.id === propertyId) ?? profileName[0];
  usePushNotification();

  const favourites = useMemo(() => {
    if (!devices) return null;
    const username = User.getUser();
    return devices.filter((device) => isFavourite(device, username));
  }, [devices]);

  const running = useMemo(() => (devices ? devices.filter((device) => device.props?.state === true) : null), [devices]);

  return (
    <Main header="home">
      <section className="hx-greet" aria-label="House">
        <div>
          <div className="hx-greet-hello">{greeting()}</div>
          <h1 className="hx-greet-home">{house?.name ?? " "}</h1>
        </div>
        <PropertyPicker />
      </section>

      <NetworkAlert />

      {error ? (
        <div className="hx-empty" role="alert" style={{ marginTop: 20 }}>
          Couldn't load this house's devices. Check your connection and reload the page.
        </div>
      ) : null}

      <HomeAddressCard house={house} />
      <RecentAutomationsSection Heading={SectionHeading} />

      <SectionHeading title="Favourites" icon={IoArrowForwardCircle} onClick={() => navigate.push("/favourites")} />
      <DeviceRow devices={favourites} emptyIcon={IoHeart} emptyText="Favourite devices are listed here." />

      <SectionHeading
        title="Running Devices"
        icon={IoArrowForwardCircle}
        onClick={() => navigate.push("/devices", { tab: "ACTIVE" })}
      />
      <DeviceRow devices={running} emptyIcon={IoPower} emptyText="No devices are running." />

      <PrivacyPolicyModal homeId={propertyId} />
    </Main>
  );
}

export default Home;
